import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
/** @import { TargetedMouseEvent } from "preact"; */

import { scimGroupMemberResponse, scimGroupResponse } from "~/backend/validate.js";
/** @import { scimOrgMappingResponse, scimRoleConflictResponse, sessionEnterpriseResponse } from "~/backend/validate.js"; */
import { ApiError, authedApiRequest, paginatedResponse, query, response } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import Title from "~/util/title.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Dropdown from "~/component/Dropdown.js";
import Icon from "~/component/Icon.js";
import Loader from "~/component/Loader.js";
import Tooltip from "~/component/Tooltip.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
/** @import { TriggerProps } from "~/component/Tooltip.js"; */
import ChipList from "~/admin/component/ChipList.js";
import ObjectPage, { ObjectPageSection } from "~/admin/component/ObjectPage.js";
import Pagination from "~/admin/component/Pagination.js";
import Table from "~/admin/component/Table.js";
import UserStack from "~/admin/component/UserStack.js";
import { changeMappingRole } from "./actions/changeMappingRole.js";
import { manageMappings } from "./actions/manageMappings.js";
import { removeMapping } from "./actions/removeMapping.js";
import Roles from "~/admin/lib/roles.js";
/** @import { RoleScope } from "~/admin/lib/roles.js"; */
import adminStyle from "~/admin/lib/style.js";
import OrgChip from "~/admin/res/org/OrgChip.js";
import util from "~/style/util.js";
import { adopt, CSS_MODULE_SCRIPTS, importCss } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./ScimMappingsDetail.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./ScimMappingsDetail.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {sessionEnterpriseResponse} enterprise
 * @property {string} scimGroupId
 */
export default class ScimMappingsDetail extends Component {
  group = query(() => ({
    key: [`/v2/enterprises/${this.props.enterprise.id}/scim-groups/${this.props.scimGroupId}`],
    keepPreviousData: true
  }), {
    schema: response(scimGroupResponse)
  });
  checked = signal(/** @type {Map<string, scimOrgMappingResponse>} */new Map());
  #setEnterpriseRole = new PromiseSignal(async (/** @type {string} */role) => {
    try {
      await authedApiRequest("PATCH", `/v2/enterprises/${this.props.enterprise.id}/scim-groups/${this.props.scimGroupId}`, {
        json: {
          enterprise_role: role
        }
      });
      Query.invalidate();
      await this.group.fetch();
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  });
  render() {
    const group = this.group.data?.data;
    return [__h(Crumb, {}, group?.name ?? __h(Loader, {})), group && __h(Title, {
      title: group.name
    }), __h(Data, {
      loading: this.group.loading && !this.group.data,
      error: this.group.error,
      empty: Boolean(this.group.data) && !group
    }, group && this.renderPage(group))];
  }

  /** @param {scimGroupResponse} group */
  renderPage(group) {
    const rows = group.mappings.map(mapping => ({
      key: mapping.id,
      label: mapping.org_name,
      value: mapping,
      columns: {
        org: __h(OrgChip, {
          org: {
            id: mapping.org_id,
            name: mapping.org_name
          }
        }),
        role: Roles.label("org", mapping.role),
        groups: __h(ChipList, {}, mapping.groups.map(g => __h("div", {
          key: g.id,
          "class": adminStyle.chip
        }, __h(Icon, {
          name: "users",
          size: 12
        }), g.group_name)))
      }
    }));
    return __h(ObjectPage, {
      backlink: {
        href: "/mappings",
        name: "Directory Mappings"
      },
      title: [__h(Icon, {
        name: "users",
        size: 22
      }), __h("span", {}, group.name)],
      desc: group.idp_id,
      actions: __h(Button, {
        size: "small",
        onClick: async (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => {
          const res = await manageMappings({
            anchor: event.currentTarget,
            placement: "bottom-start",
            enterpriseId: this.props.enterprise.id
          }, group);
          if (res) this.checked.value = new Map();
        }
      }, __h(Icon, {
        name: "pencil",
        size: 12
      }), "Manage mappings"),
      sidebar: __h(ObjectPageSection, {
        title: "Enterprise role",
        actions: __h(Dropdown, {
          id: "scim-enterprise-role",
          placement: "bottom-end",
          items: Roles.enterprise.filter(role => role.key !== group.enterprise_role).map(role => ({
            text: role.label,
            disabled: this.#setEnterpriseRole.loading,
            onSelect: async () => {
              const title = `Grant ${group.name} ${role.label} in ${this.props.enterprise.name}?`;
              if (!(await Roles.confirm(role, title))) return;
              await this.#setEnterpriseRole.invoke(role.key);
            }
          })),
          trigger: __h(Button, {
            size: "small",
            level: "tertiary",
            loading: this.#setEnterpriseRole.loading,
            disabled: this.#setEnterpriseRole.loading,
            popovertarget: "scim-enterprise-role"
          }, __h(Icon, {
            name: "key",
            size: 12
          }), "Change")
        })
      }, __h("span", {}, Roles.label("enterprise", group.enterprise_role)))
    }, __h(ObjectPageSection, {
      title: "Organizations",
      actions: __h(Button, {
        size: "small",
        level: "tertiary",
        disabled: this.checked.value.size === 0,
        onClick: async (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => {
          const res = await changeMappingRole({
            anchor: event.currentTarget,
            placement: "bottom-start",
            enterpriseId: this.props.enterprise.id
          }, group, ...this.checked.value.values());
          if (res) this.checked.value = new Map();
        }
      }, __h(Icon, {
        name: "pencil",
        size: 12
      }), "Change role")
    }, __h("div", {
      "class": util.card
    }, h(/** @type {typeof Table<scimOrgMappingResponse>} */Table, {
      label: "mapping",
      checked: this.checked.value,
      onCheck: check => this.checked.value = check,
      headers: [{
        id: "org",
        label: "Organization"
      }, {
        id: "role",
        label: "Role"
      }, {
        id: "groups",
        label: "Groups"
      }],
      empty: "Not mapped to any organization.",
      rows,
      scroll: false,
      onTripleDots: (mapping, anchor) => {
        return [{
          icon: "pencil",
          text: "Change role",
          onSelect: async () => {
            const res = await changeMappingRole({
              anchor: anchor.current,
              placement: "bottom-end",
              enterpriseId: this.props.enterprise.id
            }, group, mapping);
            if (res) this.checked.value = new Map();
          }
        }, {
          icon: "trash",
          text: "Remove",
          kind: (/** @type {const} */"negative"),
          onSelect: async () => {
            const res = await removeMapping({
              enterpriseId: this.props.enterprise.id
            }, group, mapping);
            if (res) this.checked.value = new Map();
          }
        }];
      }
    }))), __h(ObjectPageSection, {
      title: "Members"
    }, __h("div", {
      "class": util.card
    }, __h(Members, {
      enterprise: this.props.enterprise,
      scimGroupId: group.id
    }))));
  }
}

/**
 * @extends {Component<MembersProps>}
 *
 * @typedef {object} MembersProps
 * @property {sessionEnterpriseResponse} enterprise
 * @property {string} scimGroupId
 */
class Members extends Component {
  paginate = new Pagination.State({
    size: 5,
    local: true
  });
  members = query(() => ({
    key: [`/v2/enterprises/${this.props.enterprise.id}/scim-groups/${this.props.scimGroupId}/members`, this.paginate.query()],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(scimGroupMemberResponse)
  });
  render() {
    const rows = this.members.data?.data.map(member => ({
      key: member.id,
      label: member.name,
      value: member,
      columns: {
        user: __h(UserStack, {
          name: member.name,
          email: member.email
        }),
        account: member.user_id ? "Provisioned" : "Not provisioned",
        conflicts: member.conflicts.length > 0 && __h(ChipList, {}, member.conflicts.map(conflict => __h(ConflictChip, {
          key: conflict.org_id,
          conflict: conflict
        })))
      }
    }));
    return h(Data, {
      loading: this.members.loading && !this.members.data,
      error: this.members.error,
      children: h(/** @type {typeof Table<scimGroupMemberResponse>} */Table, {
        label: "Directory group member",
        headers: [{
          id: "user",
          label: "Person"
        }, {
          id: "account",
          label: "Phaze account"
        }, {
          id: "conflicts",
          label: "Conflicts"
        }],
        empty: "This directory group has no members.",
        rows: rows ?? [],
        paginate: this.paginate.props(this.members),
        scroll: false
      })
    });
  }
}

/** @param {{ conflict: scimRoleConflictResponse }} props */
function ConflictChip({
  conflict
}) {
  /** @type {{ scope: RoleScope; icon: string; name: string }} */
  const {
    scope,
    icon,
    name
  } = conflict.org_id ? {
    scope: "org",
    icon: "office",
    name: conflict.org_name
  } : {
    scope: "enterprise",
    icon: "phaze",
    name: "Enterprise"
  };
  return __h(Tooltip, {
    placement: "top",
    contents: __h("ul", {
      "class": css.votes
    }, conflict.votes.map(vote => __h("li", {
      key: vote.scim_group_id
    }, vote.scim_group_name, ": ", __h("strong", {}, Roles.label(scope, vote.role)))))
  }, (/** @type {TriggerProps} */trigger) => __h("span", {
    ...trigger,
    "class": adminStyle.chip + " " + css.conflict
  }, __h(Icon, {
    name: icon,
    size: 12
  }), name));
}